import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Code2, Eye, Loader2, Monitor, Save, Send, Smartphone, X } from 'lucide-react';
import { Alert, AlertProps } from '../../../Shared/Utils/Alert';
import {
  createEmailTemplate,
  getEmailTemplateVariables,
  previewEmailTemplate,
  testSendEmailTemplate,
  updateEmailTemplate,
} from '../../Services/EmailAdminService';
import {
  EmailTemplate,
  EmailTemplateStatus,
  EmailTemplateVariable,
} from '../../Types/EmailAdminType';

interface Props {
  template: EmailTemplate | null;
  onClose: () => void;
  onSaved: (message: string) => void;
}

const labelClass = 'mb-1 block text-[10px] font-black uppercase tracking-widest text-slate-400';
const fieldClass =
  'w-full rounded-xl border border-slate-800 bg-slate-950/60 px-3 py-2.5 text-sm font-semibold text-slate-100 outline-none transition placeholder:text-slate-600 focus:border-cyan-500/50 [color-scheme:dark]';

const STARTER_HTML = `<!DOCTYPE html>
<html>
  <body style="margin:0;padding:0;background:#0f172a;font-family:Arial,sans-serif;">
    <div style="max-width:560px;margin:0 auto;padding:32px 24px;color:#e2e8f0;">
      <h1 style="color:#22d3ee;font-size:22px;margin:0 0 12px;">Hello {{candidateName}}</h1>
      <p style="font-size:14px;line-height:1.6;">Write your message here.</p>
      <p style="margin-top:24px;font-size:11px;color:#64748b;">
        &copy; {{currentYear}} {{portalName}} · <a href="{{unsubscribeLink}}" style="color:#64748b;">Unsubscribe</a>
      </p>
    </div>
  </body>
</html>`;

/**
 * Full-screen editor for one email template: form on the left, a sandboxed live preview on the
 * right. The preview calls the same render the server uses, so what the admin sees is what a
 * recipient is delivered - the backend is the single source of truth for token substitution.
 */
function EmailTemplateEditor({ template, onClose, onSaved }: Props) {
  const isEdit = Boolean(template?.id);

  const [name, setName] = useState(template?.name ?? '');
  const [subject, setSubject] = useState(template?.subject ?? '');
  const [htmlBody, setHtmlBody] = useState(template?.htmlBody ?? STARTER_HTML);
  const [category, setCategory] = useState(template?.category ?? '');
  const [description, setDescription] = useState(template?.description ?? '');
  const [status, setStatus] = useState<EmailTemplateStatus>(template?.status ?? 'ACTIVE');

  const [variables, setVariables] = useState<EmailTemplateVariable[]>([]);
  const [preview, setPreview] = useState<{ subject: string; html: string }>({ subject: '', html: '' });
  const [previewLoading, setPreviewLoading] = useState(false);
  const [device, setDevice] = useState<'desktop' | 'mobile'>('desktop');
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [alert, setAlert] = useState<AlertProps | null>(null);
  const [savedId, setSavedId] = useState<string | undefined>(template?.id ?? undefined);

  const bodyRef = useRef<HTMLTextAreaElement>(null);

  const showAlert = (variant: AlertProps['variant'], title: string, message: string) =>
    setAlert({ variant, title, message, autoCloseMs: 5000, onClose: () => setAlert(null) });

  useEffect(() => {
    getEmailTemplateVariables()
      .then((res) => {
        if (res.success && Array.isArray(res.data)) setVariables(res.data);
      })
      .catch(() => undefined);
  }, []);

  // Debounced preview: every keystroke would otherwise be a round trip to the render endpoint.
  useEffect(() => {
    const handle = setTimeout(async () => {
      setPreviewLoading(true);
      try {
        const res = await previewEmailTemplate({ subject, htmlBody });
        if (res.success && res.data) setPreview(res.data);
      } catch {
        // Keep the last good preview rather than blanking the pane on a transient error.
      } finally {
        setPreviewLoading(false);
      }
    }, 500);
    return () => clearTimeout(handle);
  }, [subject, htmlBody]);

  const handleSave = useCallback(async () => {
    if (!name.trim()) {
      showAlert('error', 'Missing name', 'Give the template a name.');
      return;
    }
    if (!htmlBody.trim()) {
      showAlert('error', 'Missing body', 'The HTML body cannot be empty.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        subject,
        htmlBody,
        category: category || undefined,
        description: description || undefined,
        status,
      };
      const res = savedId
        ? await updateEmailTemplate(savedId, payload)
        : await createEmailTemplate(payload);
      if (res.success && res.data) {
        setSavedId(res.data.id);
        onSaved(savedId ? 'Template updated.' : 'Template created.');
      } else {
        showAlert('error', 'Save failed', res.message || 'Please try again.');
      }
    } catch {
      showAlert('error', 'Save failed', 'Please try again.');
    } finally {
      setSaving(false);
    }
  }, [name, subject, htmlBody, category, description, status, savedId, onSaved]);

  const handleTestSend = async () => {
    if (!savedId) {
      showAlert('info', 'Save first', 'Save the template before sending a test.');
      return;
    }
    if (!testEmail.trim()) {
      showAlert('error', 'Missing recipient', 'Enter an address to send the test to.');
      return;
    }
    setSending(true);
    try {
      const res = await testSendEmailTemplate(savedId, {
        toEmail: testEmail.trim(),
        subject,
        htmlBody,
      });
      if (res.success) {
        showAlert('success', 'Test sent', `A test email was sent to ${testEmail.trim()}.`);
      } else {
        showAlert('error', 'Test failed', res.message || 'The provider rejected the message.');
      }
    } catch {
      showAlert('error', 'Test failed', 'The provider rejected the message.');
    } finally {
      setSending(false);
    }
  };

  // Inserting at the caret only makes sense while the body still has focus. When the admin clicks
  // a variable chip the textarea has already blurred, and an unfocused selection sits at index 0 -
  // which is how a token ends up glued to the front of the document. Unfocused inserts therefore
  // land at the end of the body content instead, via a ref that records the last caret we saw.
  const caretRef = useRef<number | null>(null);

  const insertVariable = (token: string) => {
    const snippet = `{{${token}}}`;
    const el = bodyRef.current;
    const focused = el !== null && document.activeElement === el;

    let start: number;
    let end: number;
    if (focused) {
      start = el!.selectionStart ?? htmlBody.length;
      end = el!.selectionEnd ?? htmlBody.length;
    } else if (caretRef.current !== null) {
      start = Math.min(caretRef.current, htmlBody.length);
      end = start;
    } else {
      const closeBody = htmlBody.lastIndexOf('</body>');
      start = closeBody >= 0 ? closeBody : htmlBody.length;
      end = start;
    }

    const next = htmlBody.slice(0, start) + snippet + htmlBody.slice(end);
    setHtmlBody(next);

    const caret = start + snippet.length;
    caretRef.current = caret;
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      el.setSelectionRange(caret, caret);
    });
  };

  const usedVariables = useMemo(() => {
    const found = new Set<string>();
    const regex = /\{\{\s*([a-zA-Z0-9_]+)\s*}}/g;
    let match: RegExpExecArray | null;
    const text = `${subject}\n${htmlBody}`;
    while ((match = regex.exec(text)) !== null) found.add(match[1]);
    return found;
  }, [subject, htmlBody]);

  return (
    <div className="fixed inset-0 z-[9990] flex flex-col bg-slate-950/98 backdrop-blur-sm">
      <header className="flex flex-wrap items-center gap-3 border-b border-slate-800 px-4 py-3 md:px-6">
        <div className="flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl border border-amber-500/30 bg-amber-500/10 text-amber-400">
            <Code2 size={18} />
          </span>
          <div>
            <h2 className="text-sm font-black text-slate-100">
              {isEdit ? 'Edit Template' : 'New Template'}
            </h2>
            <p className="text-[11px] font-semibold text-slate-500">
              HTML with {'{{token}}'} placeholders
            </p>
          </div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-xl bg-cyan-500 px-4 py-2.5 text-sm font-black text-slate-950 transition hover:bg-cyan-400 disabled:opacity-50"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            Save
          </button>
          <button
            onClick={onClose}
            className="rounded-xl border border-slate-800 bg-slate-900 p-2.5 text-slate-400 transition hover:text-slate-100"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2">
          {/* Editor column */}
          <div className="flex flex-col gap-4 border-slate-800 p-4 md:p-6 lg:border-r">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className={labelClass}>Name</label>
              <input className={fieldClass} value={name} onChange={(e) => setName(e.target.value)} placeholder="Welcome email" />
            </div>
            <div>
              <label className={labelClass}>Category</label>
              <input className={fieldClass} value={category} onChange={(e) => setCategory(e.target.value)} placeholder="TRANSACTIONAL" />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-[1fr_auto]">
            <div>
              <label className={labelClass}>Subject</label>
              <input className={fieldClass} value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Welcome to {{portalName}}" />
            </div>
            <div>
              <label className={labelClass}>Status</label>
              <select className={fieldClass} value={status} onChange={(e) => setStatus(e.target.value as EmailTemplateStatus)}>
                <option value="ACTIVE">ACTIVE</option>
                <option value="DRAFT">DRAFT</option>
                <option value="INACTIVE">INACTIVE</option>
              </select>
            </div>
          </div>

          <div>
            <label className={labelClass}>Description</label>
            <input className={fieldClass} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What this template is for" />
          </div>

          <div className="flex flex-col">
            <div className="flex items-center justify-between rounded-t-xl border border-b-0 border-slate-800 bg-slate-900/70 px-3 py-2">
              <span className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-400">
                <Code2 size={13} /> HTML Body
              </span>
              <span className="text-[10px] font-semibold text-slate-500">
                {usedVariables.size} variable{usedVariables.size === 1 ? '' : 's'} in use
              </span>
            </div>
            <textarea
              ref={bodyRef}
              value={htmlBody}
              onChange={(e) => setHtmlBody(e.target.value)}
              onSelect={(e) => {
                caretRef.current = (e.target as HTMLTextAreaElement).selectionStart;
              }}
              onBlur={(e) => {
                caretRef.current = e.target.selectionStart;
              }}
              spellCheck={false}
              className="h-[380px] resize-y rounded-b-xl border border-slate-800 bg-slate-950/80 p-4 font-mono text-[13px] leading-6 text-slate-200 outline-none transition focus:border-cyan-500/50"
            />
          </div>

          <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-3">
            <p className="mb-2 text-[10px] font-black uppercase tracking-widest text-slate-400">
              Insert variable
            </p>
            <div className="flex flex-wrap gap-1.5">
              {variables.map((v) => (
                <button
                  key={v.name}
                  type="button"
                  title={`${v.description} · e.g. ${v.sample}`}
                  onClick={() => insertVariable(v.name)}
                  className={`rounded-lg border px-2 py-1 text-[10px] font-bold transition ${
                    usedVariables.has(v.name)
                      ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-400'
                      : 'border-slate-800 bg-slate-950/60 text-slate-400 hover:border-cyan-500/40 hover:text-cyan-400'
                  }`}
                >
                  {`{{${v.name}}}`}
                </button>
              ))}
              {variables.length === 0 && (
                <span className="text-[11px] font-semibold text-slate-600">Loading variables…</span>
              )}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-3">
            <p className="mb-2 text-[10px] font-black uppercase tracking-widest text-slate-400">
              Send a test
            </p>
            <div className="flex flex-col gap-2 sm:flex-row">
              <input
                className={fieldClass}
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="you@example.com"
              />
              <button
                onClick={handleTestSend}
                disabled={sending || !savedId}
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-cyan-500/40 bg-cyan-500/10 px-4 py-2.5 text-sm font-black text-cyan-400 transition hover:bg-cyan-500/20 disabled:opacity-50"
              >
                {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                Test
              </button>
            </div>
            {!savedId && (
              <p className="mt-1.5 text-[10px] font-semibold text-slate-600">
                Save the template before sending a test.
              </p>
            )}
          </div>
        </div>

          {/* Preview column */}
          <div className="flex flex-col gap-3 bg-slate-950 p-4 md:p-6 lg:sticky lg:top-0 lg:self-start">
          <div className="mb-3 flex items-center gap-3">
            <span className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-400">
              <Eye size={14} /> Live preview
            </span>
            {previewLoading && <Loader2 size={14} className="animate-spin text-cyan-500" />}
            <div className="ml-auto flex gap-1 rounded-xl border border-slate-800 bg-slate-900 p-1">
              <button
                onClick={() => setDevice('desktop')}
                className={`rounded-lg p-1.5 transition ${device === 'desktop' ? 'bg-cyan-500/20 text-cyan-400' : 'text-slate-500 hover:text-slate-300'}`}
                title="Desktop"
              >
                <Monitor size={14} />
              </button>
              <button
                onClick={() => setDevice('mobile')}
                className={`rounded-lg p-1.5 transition ${device === 'mobile' ? 'bg-cyan-500/20 text-cyan-400' : 'text-slate-500 hover:text-slate-300'}`}
                title="Mobile"
              >
                <Smartphone size={14} />
              </button>
            </div>
          </div>

          <div className="mb-2 rounded-lg border border-slate-800 bg-slate-900/60 px-3 py-2">
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">Subject</p>
            <p className="truncate text-sm font-semibold text-slate-200">
              {preview.subject || subject || '—'}
            </p>
          </div>

            <div className="flex h-[520px] items-start justify-center overflow-hidden rounded-2xl border border-slate-800 bg-white lg:h-[calc(100vh-260px)]">
              <iframe
                title="Email preview"
                sandbox=""
                srcDoc={preview.html || htmlBody}
                className="h-full w-full"
                style={{ maxWidth: device === 'mobile' ? 380 : '100%' }}
              />
            </div>
          </div>
        </div>
      </div>

      {alert && (
        <div className="fixed bottom-4 right-4 z-[9999]">
          <Alert {...alert} onClose={() => setAlert(null)} />
        </div>
      )}
    </div>
  );
}

export default EmailTemplateEditor;
